import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  banUser,
  softDeleteUser,
  unbanUser,
  listAdminUsers,
  getAdminUserDetail,
  editAdminUserBalance,
  setAdminUserTierOverride,
  setAdminUserRefundStatus,
  revealAdminUserApiKey,
  regenerateAdminUserApiKey,
  revokeAdminUserApiKey,
  postUserTelegramUnlink,
  type AdminUserListResponse,
  type AdminUserDetailResponse,
  type AdminBalanceEditInput,
  type AdminBalanceEditResponse,
  type AdminTierOverrideResponse,
  type AdminRefundStatusResponse,
  type WireUserTierView,
  type AdminApiKeyRevealResponse,
  type AdminApiKeyRegenerateResponse,
  type AdminApiKeyRevokeResponse,
  type AdminUserTelegramUnlinkResponse,
  type ModerationInput,
  type ModerationResponse,
  type WireAdminUserListEntry,
  type WireAdminUserDetail,
  type WireAdminUserDetailOrder,
  type WireAdminUserDetailTransaction,
  type WireAdminUserDetailApiKey,
} from '../api/endpoints.js';
import { useApiMutation, useApiQuery, useDebouncedValue, useVisiblePolling } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  IdMono,
  Input,
  LoadingState,
  Modal,
  PageHead,
  Select,
  StatCard,
  StatusBadge,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import { CopyButton } from './user-inspect/CopyButton.js';
import { InfoItem, InfoList } from './user-inspect/InfoList.js';
import { SoftBreaks } from './user-inspect/SoftBreaks.js';
import { OrderRateLimitCard } from './user-inspect/OrderRateLimitCard.js';
import { AdminNoteSection } from './user-inspect/AdminNoteSection.js';
import '../styles/user-inspect.css';
import '../styles/users.mobile.css';

const PAGE_SIZE = 50;

type StatusFilter = 'all' | 'active' | 'banned' | 'deleted';
type SortBy = 'createdAt' | 'updatedAt' | 'email' | 'username' | 'balance' | 'isOnlineNow';
type SortOrder = 'asc' | 'desc';

/** micro-USD → "$X.XX" */
function microToUsd(micro: number): string {
  return `$${(micro / 1_000_000).toFixed(2)}`;
}
/** basis points → "X.XX%" */
function bpsToPct(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}
/** Compact tier label for the list column. */
function tierLabel(tier: WireUserTierView): string {
  const lt = tier.lifetime.name ?? '—';
  const wk = tier.weekly.name ?? '—';
  return `${lt} / ${wk}`;
}

export function UserInspectPage(): JSX.Element {
  const { actorId: routeId } = useParams();
  const actorId = routeId ?? '';
  // key: moving to another account starts a fresh detail panel (loading, then
  // that account's data). Without it the stale-while-revalidate query kept the
  // previous account on screen, and every card's action targeted the new one.
  return actorId ? <UserDetailPanel key={actorId} actorId={actorId} /> : <UserListPanel />;
}

// ---------------------------------------------------------------------------
// List panel
// ---------------------------------------------------------------------------

function UserListPanel(): JSX.Element {
  const navigate = useNavigate();
  const canList = useCan('user:list');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Live search: query per typing-pause (debounced), no button click needed.
  // The form's onSubmit still flushes immediately on Enter.
  const debouncedSearch = useDebouncedValue(searchDraft, 300);
  useEffect(() => {
    setSearch(debouncedSearch.trim());
    setPage(1);
  }, [debouncedSearch]);

  const query = useApiQuery<AdminUserListResponse>(
    (options) =>
      listAdminUsers(options, {
        page,
        limit: PAGE_SIZE,
        search,
        status,
        sortBy,
        sortOrder,
      }),
    [page, search, status, sortBy, sortOrder],
  );
  useVisiblePolling(query.reload, 5000, canList);

  function onSubmitSearch(ev: FormEvent): void {
    ev.preventDefault();
    setPage(1);
    setSearch(searchDraft.trim());
  }

  // Click a sortable column header: toggle direction if already active, else
  // switch to it (default DESC). Global sort — applies to the whole list.
  function onSort(col: SortBy): void {
    if (sortBy === col) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
    setPage(1);
  }

  if (!canList) {
    return (
      <div>
        <PageHead
          crumb={<>Yönetim <Icon.ChevronRight size={11} /> Kullanıcılar</>}
          title="Kullanıcılar"
          subtitle="Listeleme için yetki gerekiyor."
        />
        <ErrorState reason="capability_missing:user:list" />
      </div>
    );
  }

  return (
    <div className="users-list-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Kullanıcılar</>}
        title="Kullanıcılar"
        subtitle={`Tüm kullanıcılar — arama, durum filtresi ve sayfa başına ${PAGE_SIZE} kayıt. Detay için satıra tıklayın.`}
      />

      <Card>
        <form onSubmit={onSubmitSearch} aria-label="kullanıcı arama">
          {/* Sizes live in styles/users.mobile.css (desktop: one row; phone:
              search + status full width, sort pair side by side, Ara full width). */}
          <div className="users-filters">
            <div className="users-filter-search">
              <Input
                type="search"
                placeholder="E-posta / kullanıcı adı ara…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                aria-label="arama"
                iconLeft={<Icon.Search size={14} />}
              />
            </div>
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
              aria-label="durum filtresi"
              className="users-filter-status"
            >
              <option value="all">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="banned">Banlı</option>
              <option value="deleted">Silinmiş</option>
            </Select>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              aria-label="sıralama"
              className="users-filter-sort"
            >
              <option value="createdAt">Kayıt tarihi</option>
              <option value="updatedAt">Son güncelleme</option>
              <option value="email">E-posta</option>
              <option value="username">Kullanıcı adı</option>
              <option value="balance">Bakiye</option>
              <option value="isOnlineNow">Çevrim içi</option>
            </Select>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              aria-label="sıralama yönü"
              className="users-filter-order"
            >
              <option value="desc">↓ Azalan</option>
              <option value="asc">↑ Artan</option>
            </Select>
            <Button type="submit" variant="primary" className="users-filter-submit" icon={<Icon.Search size={14} />}>
              Ara
            </Button>
          </div>
        </form>
      </Card>

      {query.kind === 'loading' ? <LoadingState /> : null}
      {query.refreshError && <Banner tone="warn">Liste güncellenemedi; aktiflik bilgisi bilinmiyor.</Banner>}
      {query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={query.reload} />
      ) : null}
      {query.kind === 'ok' ? (
        <UserListResults
          data={query.data.data}
          presenceStale={!!query.refreshError}
          page={page}
          onPageChange={setPage}
          onOpen={(actor) => navigate(`/users/${encodeURIComponent(actor)}`)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
        />
      ) : null}
    </div>
  );
}

function UserListResults({
  data,
  presenceStale,
  page,
  onPageChange,
  onOpen,
  sortBy,
  sortOrder,
  onSort,
}: {
  readonly data: AdminUserListResponse['data'];
  readonly presenceStale: boolean;
  readonly page: number;
  readonly onPageChange: (n: number) => void;
  readonly onOpen: (actorId: string) => void;
  readonly sortBy: SortBy;
  readonly sortOrder: SortOrder;
  readonly onSort: (col: SortBy) => void;
}): JSX.Element {
  if (data.users.length === 0) {
    return <EmptyState label="Bu filtrelerle eşleşen kullanıcı yok." />;
  }
  // Clickable sort header — uses the existing TH pass-through (onClick + style),
  // no shared-component change. Arrow shows the active column + direction.
  // Header wrapping lives in styles/users.mobile.css (.users-th-sort: one
  // line; .users-th-wrap may take two lines on desktop so E-posta gets room).
  const SortTH = ({
    col,
    className,
    children,
  }: {
    readonly col: SortBy;
    readonly className?: string;
    readonly children: React.ReactNode;
  }): JSX.Element => (
    <TH
      onClick={() => onSort(col)}
      className={className ? `users-th-sort ${className}` : 'users-th-sort'}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Sırala"
    >
      {children}
      <span style={{ opacity: sortBy === col ? 1 : 0.25, marginLeft: 4 }}>
        {sortBy === col ? (sortOrder === 'desc' ? '↓' : '↑') : '↕'}
      </span>
    </TH>
  );
  return (
    <Card flush>
      <CardHeader
        title={
          <span className="t-muted">
            Toplam: <strong style={{ color: 'var(--text-primary)' }}>{data.total.toLocaleString('tr-TR')}</strong>{' '}
            (sayfa {data.currentPage} / {data.totalPages})
          </span>
        }
      />
      <CardBody tight style={{ padding: 0 }}>
        {/* ≤1080px: every row is a stacked card (email + badges on top, the
            other columns as labelled lines). Sorting stays reachable through
            the "sıralama" / "sıralama yönü" selects above. 1081–1279px (sidebar
            open): the nine columns scroll inside the card body (styles.css
            §10.1 contains a direct-child table, with edge shadows). */}
        <Table dense stack className="tbl-stack-xl users-table" data-testid="admin-users-table">
          <THead>
            <TRow>
              <SortTH col="email">E-posta</SortTH>
              <SortTH col="username" className="users-th-wrap">Kullanıcı adı</SortTH>
              {/* "Durum" was aktif/pasif, which is true of almost every row and
                  told an operator nothing. What matters when scanning the list
                  is whether the account verified its e-mail. Ban / soft-delete
                  are exceptional states, so they still surface here as a second
                  chip rather than occupying the column on their own. */}
              <TH>Doğrulama</TH>
              <SortTH col="isOnlineNow">Aktiflik</SortTH>
              <TH className="users-th-tier">Seviye (Kalıcı / Haftalık)</TH>
              <TH>İndirim</TH>
              <TH>İade</TH>
              <SortTH col="balance">Bakiye</SortTH>
              <SortTH col="createdAt" className="users-th-wrap">Kayıt tarihi</SortTH>
            </TRow>
          </THead>
          <TBody>
            {data.users.map((u) => (
              <UserListRow key={u.actorId} u={u} onOpen={onOpen} presenceStale={presenceStale} />
            ))}
          </TBody>
        </Table>
      </CardBody>
      <div style={{ padding: 12 }}>
        <Pagination page={page} totalPages={data.totalPages} onChange={onPageChange} />
      </div>
    </Card>
  );
}

function UserListRow({
  u,
  onOpen,
  presenceStale,
}: {
  readonly u: WireAdminUserListEntry;
  readonly onOpen: (actorId: string) => void;
  readonly presenceStale: boolean;
}): JSX.Element {
  const name = u.username ?? u.displayName ?? null;
  return (
    <TRow
      clickable
      onClick={() => onOpen(u.actorId)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(u.actorId);
      }}
    >
      {/* Stacked card: the e-mail is the identity line; an account without
          an e-mail shows its name there instead (presentation only). A
          narrow column wraps an e-mail / username at "@", "." or "_". */}
      <TD label="E-posta" className={u.email ? 'users-cell-email tbl-stack-primary' : 'users-cell-email tbl-stack-hide'}>
        {u.email != null ? <SoftBreaks text={u.email} /> : '—'}
      </TD>
      <TD label="Kullanıcı adı" className={u.email ? 'users-cell-name' : 'users-cell-name tbl-stack-primary'}>
        {name != null ? <SoftBreaks text={name} /> : '—'}
      </TD>
      <TD label="Doğrulama" className="tbl-stack-status">
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {u.emailVerified ? (
            <Badge tone="success" dot>doğrulanmış</Badge>
          ) : (
            <Badge tone="warn" dot>doğrulanmamış</Badge>
          )}
          {exceptionalStatusBadge(u)}
        </span>
      </TD>
      <TD label="Aktiflik" className="t-xs">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            className={!presenceStale && u.presenceAvailable !== false && u.isOnlineNow ? 'kop-presence-dot kop-presence-dot--on' : 'kop-presence-dot'}
            aria-hidden="true"
          />
          {presenceStale || u.presenceAvailable === false ? 'bilinmiyor' : u.isOnlineNow ? 'çevrimiçi' : `${u.lastActiveSource === 'last_login' ? 'Giriş: ' : ''}${presenceRelative(u.lastActiveAt)}`}
        </span>
      </TD>
      <TD label="Seviye" className="t-xs users-cell-tier">{tierLabel(u.tier)}</TD>
      <TD label="İndirim" className="t-mono t-num cell-nowrap">{bpsToPct(u.tier.effectiveDiscountBps)}</TD>
      <TD label="İade">{u.refundDisabled ? <Icon.X size={14} /> : <Icon.Check size={14} />}</TD>
      <TD label="Bakiye" className="t-mono t-num cell-nowrap">${u.balance.toFixed(2)}</TD>
      <TD label="Kayıt tarihi" className="t-mono t-xs t-tertiary cell-nowrap">{u.createdAt.slice(0, 10)}</TD>
    </TRow>
  );
}

/**
 * Only the states an operator has to react to. A plain "aktif" row renders
 * nothing here — the verification chip next to it already carries the signal.
 */
function exceptionalStatusBadge(u: WireAdminUserListEntry): JSX.Element | null {
  if (u.isBanned) return <Badge tone="danger" dot>banlı</Badge>;
  if (u.isDeleted) return <Badge tone="danger" dot>silinmiş</Badge>;
  return null;
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  readonly page: number;
  readonly totalPages: number;
  readonly onChange: (n: number) => void;
}): JSX.Element {
  return (
    <div className="users-pager" style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} icon={<Icon.ChevronLeft size={14} />}>
        Önceki
      </Button>
      <span className="t-muted t-small users-pager-label">
        Sayfa {page} / {totalPages}
      </span>
      <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)} iconRight={<Icon.ChevronRight size={14} />}>
        Sonraki
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function UserDetailPanel({ actorId }: { readonly actorId: string }): JSX.Element {
  const navigate = useNavigate();
  const canViewDetail = useCan('user:view_detail');
  const canBan = useCan('user:ban');
  const canUnban = useCan('user:unban');
  const canDelete = useCan('user:delete');
  const canAdjust = useCan('user:adjust_balance');

  const query = useApiQuery<AdminUserDetailResponse>(
    (options) => getAdminUserDetail(options, actorId),
    [actorId],
  );

  const reload = query.reload;
  useVisiblePolling(reload, 5000, canViewDetail);

  if (!canViewDetail) {
    return (
      <div>
        <PageHead title="Kullanıcı Detayı" />
        <ErrorState reason="capability_missing:user:view_detail" />
      </div>
    );
  }

  const user = query.kind === 'ok' ? query.data.data.user : null;
  const presenceStale = !!query.refreshError;

  return (
    <div className="uinspect">
      {/* The header answers "who is this?" before anything else: the e-mail
          is the title and the account's state sits under it as badges, so
          the status no longer hides in a row halfway down the identity card. */}
      <PageHead
        crumb={
          <>
            <Link to="/users" style={{ color: 'var(--text-tertiary)' }}>Kullanıcılar</Link>
            <Icon.ChevronRight size={11} /> Detay
          </>
        }
        title={user ? <UserTitleText title={userTitle(user)} /> : 'Kullanıcı Detayı'}
        titleLabel={user ? userTitle(user) : undefined}
        subtitle={user ? <UserHeadBadges user={user} presenceStale={presenceStale} /> : undefined}
        actions={
          <>
            {user ? (
              <Link
                className="btn btn-secondary"
                to={`/messages?actor=${encodeURIComponent(user.actorId)}`}
                data-testid="user-open-chat"
              >
                <Icon.Send size={14} /> Sohbeti aç
              </Link>
            ) : null}
            <Button variant="secondary" icon={<Icon.Back size={14} />} onClick={() => navigate('/users')}>
              Listeye dön
            </Button>
          </>
        }
      />
      {query.kind === 'loading' ? <LoadingState /> : null}
      {query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={reload} />
      ) : null}
      {query.kind === 'ok' ? (
        <DetailBody
          data={query.data.data}
          presenceStale={presenceStale}
          actorId={actorId}
          canBan={canBan}
          canUnban={canUnban}
          canDelete={canDelete}
          canAdjust={canAdjust}
          onChanged={reload}
        />
      ) : null}
    </div>
  );
}

/** The page title: the e-mail, or the best identifier the account has. */
function userTitle(user: WireAdminUserDetail): string {
  return user.email ?? user.username ?? user.displayName ?? user.actorId;
}

/**
 * The page title: an e-mail / username / id wraps at its "@", "." or "_"
 * (SoftBreaks, as in the Kimlik card) instead of mid-word on a phone; a
 * display name with spaces wraps between its words as before.
 */
function UserTitleText({ title }: { readonly title: string }): JSX.Element {
  return /\s/.test(title) ? <>{title}</> : <SoftBreaks text={title} />;
}

type Presence = 'online' | 'offline' | 'unknown';

/** D-231 F6 — real-activity presence; a failed refresh is "unknown", never a stale "online". */
function presenceOf(user: WireAdminUserDetail, presenceStale: boolean): Presence {
  if (presenceStale || user.presenceAvailable === false) return 'unknown';
  return user.isOnlineNow ? 'online' : 'offline';
}

/** Account state, verification, presence and the refund flag as one badge row. */
function UserHeadBadges({
  user,
  presenceStale,
}: {
  readonly user: WireAdminUserDetail;
  readonly presenceStale: boolean;
}): JSX.Element {
  const presence = presenceOf(user, presenceStale);
  return (
    // Touch screens print the stale-presence tooltip as a last line (shared
    // [data-touch-detail]; the text content is unchanged).
    <span
      className="uinspect-head-meta"
      data-testid="user-head-badges"
      data-touch-detail={presence === 'unknown' && presenceStale ? 'Aktiflik bilgisi güncellenemedi' : undefined}
    >
      {statusBadgeFor(user)}
      {user.emailVerified ? (
        <Badge tone="success" dot>e-posta doğrulanmış</Badge>
      ) : (
        <Badge tone="warn" dot>e-posta doğrulanmamış</Badge>
      )}
      {presence === 'online' ? (
        <Badge tone="success" dot>çevrimiçi</Badge>
      ) : presence === 'offline' ? (
        <Badge tone="neutral" dot>çevrimdışı</Badge>
      ) : (
        <Badge tone="neutral" title={presenceStale ? 'Aktiflik bilgisi güncellenemedi' : undefined}>
          aktiflik bilinmiyor
        </Badge>
      )}
      {user.refundDisabled ? <Badge tone="orange" dot>iade kapalı</Badge> : null}
    </span>
  );
}

function DetailBody({
  data,
  presenceStale,
  actorId,
  canBan,
  canUnban,
  canDelete,
  canAdjust,
  onChanged,
}: {
  readonly data: AdminUserDetailResponse['data'];
  readonly presenceStale: boolean;
  readonly actorId: string;
  readonly canBan: boolean;
  readonly canUnban: boolean;
  readonly canDelete: boolean;
  readonly canAdjust: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const totals = { total: data.totalOrdersCount, completed: data.completedOrdersCount };
  return (
    <div className="uinspect-body">
      {/* The four numbers a support question usually starts with, before any
          card has to be opened — one equal-height row. */}
      <UserHeadline user={data.user} topups={data.topups} totals={totals} presenceStale={presenceStale} />
      {/*
        Two independent columns on wide screens (identity + tier on the left,
        money and access on the right) instead of one auto-fit grid of six
        cards: a shared grid row stretched the short cards to the height of
        the tallest one, which is what made some boxes look huge and others
        tiny. Each column now stacks at its own height. Below 1200px the
        columns dissolve (display: contents) and the cards follow DOM order on
        a single column (Kimlik, Seviye, Bakiye, İade, Sipariş limiti, API). No CSS `order`:
        a visual order that differs from the DOM makes keyboard/screen-reader
        focus jump between cards (WCAG 2.4.3).
      */}
      <div className="uinspect-split" data-testid="user-detail-split">
        <div className="uinspect-stack">
          <IdentityCard
            user={data.user}
            telegram={telegramStateOf(data)}
            canAdjust={canAdjust}
            actorId={actorId}
            onChanged={onChanged}
          />
          <TierCard user={data.user} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
        </div>
        <div className="uinspect-stack">
          <BalanceCard
            user={data.user}
            topups={data.topups}
            canAdjust={canAdjust}
            actorId={actorId}
            onChanged={onChanged}
          />
          <RefundCard user={data.user} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
          <OrderRateLimitCard
            key={actorId}
            user={data.user}
            canAdjust={canAdjust}
            actorId={actorId}
            onChanged={onChanged}
          />
          <ApiKeyCard apiKeys={data.apiKeys} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
        </div>
      </div>
      {/* The order table carries site + mailbox, so it needs the full
          width; squeezing it into a half column reintroduced the h-scroll. */}
      <RecentOrdersCard orders={data.orders} />
      <RecentTransactionsCard transactions={data.transactions} />
      {/* Ban / unban / soft delete last: rare, destructive, and always behind a reason. */}
      <ModerationCard
        user={data.user}
        canBan={canBan}
        canUnban={canUnban}
        canDelete={canDelete}
        actorId={actorId}
        onChanged={onChanged}
      />
    </div>
  );
}

/** Balance, top-ups, orders and last activity, at a glance. */
function UserHeadline({
  user,
  topups,
  totals,
  presenceStale,
}: {
  readonly user: WireAdminUserDetail;
  readonly topups: AdminUserDetailResponse['data']['topups'];
  readonly totals: { readonly total: number; readonly completed: number };
  readonly presenceStale: boolean;
}): JSX.Element {
  const presence = presenceOf(user, presenceStale);
  const topupValue = !topups ? '—' : topups.count > 0 ? microToUsd(topups.totalMicro) : 'hiç';
  const topupMeta = !topups
    ? 'bu API sürümünde yok'
    : topups.count > 0
      ? `${topups.count.toLocaleString('tr-TR')} yükleme · sonuncusu ${sinceLabel(topups.lastAt)}`
      : 'ödeme kaydı yok';
  const lastLoginMeta = `Son giriş: ${user.lastLogin ? sinceLabel(user.lastLogin) : 'kayıt yok'}`;
  // D-231: with live presence unknown (Redis presence down or a failed
  // refresh) the API still sends lastActiveAt — the last_login fallback, or
  // the last value we fetched. Show that age like the old identity row did
  // ("kayıt yok" only when there is no timestamp at all).
  const activityValue = presence === 'online' ? 'şu an' : sinceLabel(user.lastActiveAt);
  const activityLabel =
    user.lastActiveSource === 'last_login' && user.lastActiveAt ? 'Son aktif (giriş kaydı)' : 'Son aktif';
  const activityMeta =
    presence !== 'unknown' ? lastLoginMeta : presenceStale ? 'güncellenemedi' : 'canlı aktiflik yok';
  return (
    <section className="uinspect-kpis" data-testid="user-kpi-grid" aria-label="Özet">
      <StatCard
        testId="user-kpi-balance"
        label="Bakiye"
        value={`$${user.balance.toFixed(2)}`}
        meta={`İndirim ${bpsToPct(user.tier.effectiveDiscountBps)} · ${tierLabel(user.tier)}`}
        icon={<Icon.Wallet size={15} />}
        iconTone={user.balance > 0 ? 'success' : 'neutral'}
      />
      <StatCard
        testId="user-kpi-topups"
        label="Toplam yükleme"
        value={topupValue}
        meta={topupMeta}
        icon={<Icon.Coins size={15} />}
        iconTone={topups && topups.count > 0 ? 'info' : 'warn'}
      />
      <StatCard
        testId="user-kpi-orders"
        label="Sipariş"
        value={totals.total.toLocaleString('tr-TR')}
        meta={`${totals.completed.toLocaleString('tr-TR')} tamamlandı`}
        icon={<Icon.Inbox size={15} />}
        iconTone="neutral"
      />
      <StatCard
        testId="user-kpi-activity"
        label={activityLabel}
        value={activityValue}
        meta={activityMeta}
        icon={<Icon.Activity size={15} />}
        iconTone={presence === 'online' ? 'success' : 'neutral'}
      />
    </section>
  );
}

/**
 * Telegram channel-reward link (contract C2, D-235).
 *
 * `null` on the wire means "not linked"; a missing field means an API build
 * that predates C2 — that is shown as unknown, never as "Bağlı değil". The
 * API may put the object under `user.telegram` or next to `user`; both are
 * read. Parsed defensively (a numeric id or an "@name" username still
 * render) so a small wire drift does not blank the section.
 */
type TelegramRewardState = 'none' | 'granted' | 'revoked';

interface TelegramLink {
  readonly userId: string;
  readonly username: string | null;
  readonly linkedAt: string | null;
  readonly linkedVia: string | null;
  readonly member: boolean | null;
  readonly memberCheckedAt: string | null;
  readonly reward: {
    readonly state: TelegramRewardState;
    readonly grantedAt: string | null;
    readonly revokedAt: string | null;
    readonly balanceWentNegative: boolean;
  };
}

type TelegramState =
  | { readonly kind: 'linked'; readonly link: TelegramLink }
  | { readonly kind: 'unlinked' }
  | { readonly kind: 'unknown'; readonly note: string };

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function telegramStateOf(data: AdminUserDetailResponse['data']): TelegramState {
  const raw: unknown = data.user.telegram !== undefined ? data.user.telegram : data.telegram;
  if (raw === undefined) return { kind: 'unknown', note: 'Bu API sürümünde yok' };
  if (raw === null) return { kind: 'unlinked' };
  if (typeof raw !== 'object') return { kind: 'unknown', note: 'Okunamadı' };
  const t = raw as Record<string, unknown>;
  const userId = typeof t.userId === 'string' || typeof t.userId === 'number' ? String(t.userId).trim() : '';
  if (!userId) return { kind: 'unknown', note: 'Okunamadı' };
  const r = (t.reward && typeof t.reward === 'object' ? t.reward : {}) as Record<string, unknown>;
  const state: TelegramRewardState = r.state === 'granted' || r.state === 'revoked' ? r.state : 'none';
  return {
    kind: 'linked',
    link: {
      userId,
      username: strOrNull(t.username)?.replace(/^@/, '') || null,
      linkedAt: strOrNull(t.linkedAt),
      linkedVia: strOrNull(t.linkedVia),
      member: typeof t.member === 'boolean' ? t.member : null,
      memberCheckedAt: strOrNull(t.memberCheckedAt),
      reward: {
        state,
        grantedAt: strOrNull(r.grantedAt),
        revokedAt: strOrNull(r.revokedAt),
        balanceWentNegative: r.balanceWentNegative === true,
      },
    },
  };
}

/** Telegram usernames are [A-Za-z0-9_]; anything else is shown but never linked. */
const TELEGRAM_USERNAME_RE = /^[A-Za-z0-9_]{1,64}$/;

/**
 * "20.09" — the reward line only needs day and month; the full stamp is in the
 * tooltip. Built by hand: CLDR's tr-TR day+month pattern is "20/09".
 */
function fmtDayMonth(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;
}

function linkedViaLabel(via: string | null): { readonly text: string; readonly title: string } {
  if (via === 'site') return { text: 'site', title: 'Müşteri panelindeki tek kullanımlık bağlantı ile' };
  if (via === 'bot_email') return { text: 'bot', title: 'Bot üzerinden e-posta + 6 haneli kod ile' };
  return { text: via ?? 'bilinmiyor', title: 'Bağlantı yolu' };
}

function TelegramMembership({ link }: { readonly link: TelegramLink }): JSX.Element {
  const checked = link.memberCheckedAt
    ? `son kontrol ${sinceLabel(link.memberCheckedAt)}`
    : 'hiç kontrol edilmedi';
  // data-touch-detail: touch screens show the exact check time (the note's
  // tooltip) as a last line; the text content stays the same.
  return (
    <span className="uinspect-inline-values" data-touch-detail={link.memberCheckedAt ? fmtDate(link.memberCheckedAt) : undefined}>
      {link.member === true ? (
        <Badge tone="success" dot>üye</Badge>
      ) : link.member === false ? (
        <Badge tone="warn" dot>üye değil</Badge>
      ) : (
        <Badge tone="neutral">bilinmiyor</Badge>
      )}
      <span className="uinspect-tg-note" title={link.memberCheckedAt ? fmtDate(link.memberCheckedAt) : undefined}>
        {checked}
      </span>
    </span>
  );
}

/** Tooltip of the "bakiye eksiye düştü" badge (repeated on touch screens). */
const NEGATIVE_BALANCE_HINT = 'Kanal ayrılma kesintisi bakiyeyi sıfırın altına indirdi';

function TelegramReward({ link }: { readonly link: TelegramLink }): JSX.Element {
  const { reward } = link;
  const granted = fmtDayMonth(reward.grantedAt);
  const revoked = fmtDayMonth(reward.revokedAt);
  const title = [
    reward.grantedAt ? `verildi ${fmtDate(reward.grantedAt)}` : null,
    reward.revokedAt ? `geri alındı ${fmtDate(reward.revokedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <span
      className="uinspect-inline-values"
      data-touch-detail={
        [title, reward.balanceWentNegative ? NEGATIVE_BALANCE_HINT : null].filter(Boolean).join(' · ') || undefined
      }
    >
      {reward.state === 'granted' ? (
        <Badge tone="success" dot title={title || undefined}>
          {granted ? `verildi ${granted}` : 'verildi'}
        </Badge>
      ) : reward.state === 'revoked' ? (
        <Badge tone="orange" dot title={title || undefined}>
          {revoked ? `geri alındı ${revoked}` : 'geri alındı'}
        </Badge>
      ) : (
        <Badge tone="neutral">yok</Badge>
      )}
      {/* D-235: leaving the channel debits $1 even when that takes the balance below zero. */}
      {reward.balanceWentNegative ? (
        <Badge tone="warn" title={NEGATIVE_BALANCE_HINT}>
          bakiye eksiye düştü
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * The Telegram block of the identity list. It sits at the end of the list,
 * after the actor id, and opens with a full-width divider item so its rows do
 * not read as part of the core identity. Same <dt>/<dd> markup as InfoItem.
 */
function TelegramInfoItems({ telegram }: { readonly telegram: TelegramState }): JSX.Element {
  const link = telegram.kind === 'linked' ? telegram.link : null;
  const username = link?.username ?? null;
  return (
    <>
      <div
        className="uinspect-info-item uinspect-info-item--wide uinspect-info-item--section"
        data-testid="user-telegram"
      >
        <dt className="uinspect-info-label">Telegram</dt>
        <dd className="uinspect-info-value">
          {telegram.kind === 'unknown' ? (
            <span className="uinspect-muted">{telegram.note}</span>
          ) : !link ? (
            <span className="uinspect-muted">Bağlı değil</span>
          ) : username ? (
            <>
              {TELEGRAM_USERNAME_RE.test(username) ? (
                <a
                  className="uinspect-info-text"
                  href={`https://t.me/${username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="user-telegram-username"
                >
                  @<SoftBreaks text={username} />
                </a>
              ) : (
                <span className="uinspect-info-text" data-testid="user-telegram-username">@<SoftBreaks text={username} /></span>
              )}
              <CopyButton value={`@${username}`} label="Telegram kullanıcı adını kopyala" />
            </>
          ) : (
            <span className="uinspect-muted" data-testid="user-telegram-username">kullanıcı adı yok</span>
          )}
        </dd>
      </div>
      {link ? (
        <>
          <InfoItem label="Telegram ID" testId="user-telegram-id">
            <IdMono value={link.userId} display={link.userId} title="Telegram ID — kopyalamak için tıklayın" />
          </InfoItem>
          <InfoItem label="Bağlandı" testId="user-telegram-linked">
            <span className="uinspect-inline-values" data-touch-detail={linkedViaLabel(link.linkedVia).title}>
              <span className="uinspect-mono uinspect-tg-when">{fmtDate(link.linkedAt)}</span>
              <Badge tone="neutral" title={linkedViaLabel(link.linkedVia).title}>
                {linkedViaLabel(link.linkedVia).text}
              </Badge>
            </span>
          </InfoItem>
          <InfoItem label="Kanal üyeliği" testId="user-telegram-member">
            <TelegramMembership link={link} />
          </InfoItem>
          <InfoItem label="Ödül durumu" testId="user-telegram-reward">
            <TelegramReward link={link} />
          </InfoItem>
        </>
      ) : null}
    </>
  );
}

/**
 * Admin-only unlink (customers cannot unlink). Stays mounted across the
 * reload that follows a successful unlink, so the confirmation survives the
 * section switching to "Bağlı değil". The modal keeps a snapshot of the link
 * it was opened for: a poll that drops the link mid-dialog must not blank it.
 */
const UNLINK_REASON_MIN = 4;

function TelegramUnlinkAction({
  telegram,
  actorId,
  onChanged,
}: {
  readonly telegram: TelegramState;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element | null {
  const [target, setTarget] = useState<TelegramLink | null>(null);
  /** The link the last successful unlink removed (drives the success banner). */
  const [removed, setRemoved] = useState<TelegramLink | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const mut = useApiMutation<string, AdminUserTelegramUnlinkResponse>(
    (opts, r) => postUserTelegramUnlink(opts, actorId, r),
  );
  const running = mut.state.kind === 'running';
  const trimmed = reason.trim();
  const valid = trimmed.length >= UNLINK_REASON_MIN;
  const link = telegram.kind === 'linked' ? telegram.link : null;

  function open(l: TelegramLink): void {
    mut.reset();
    setRemoved(null);
    setReason('');
    setTouched(false);
    setTarget(l);
  }
  function close(): void {
    if (running) return;
    setTarget(null);
  }
  async function onConfirm(ev?: FormEvent): Promise<void> {
    ev?.preventDefault();
    setTouched(true);
    if (!valid || running) return;
    const unlinking = target;
    const res = await mut.run(trimmed);
    if (res.kind === 'ok') {
      setRemoved(unlinking);
      setTarget(null);
      setReason('');
      onChanged();
    } else if (res.kind === 'error' && res.reason === 'not_linked') {
      // Someone else already unlinked it — refresh so the section says so.
      onChanged();
    }
  }

  // The banner describes the link that was removed. A NEW link (the customer
  // linked again after the unlink) retires it; the removed link still showing
  // (reload failed, or an in-flight poll landed late) does not — the unlink
  // did succeed, and the stale-while-revalidate data is what lags.
  const relinked =
    link !== null &&
    (removed === null || link.userId !== removed.userId || link.linkedAt !== removed.linkedAt);
  const done = mut.state.kind === 'ok' && target === null && !relinked;
  if (!link && !done && target === null) return null;
  const who = target ? (target.username ? `@${target.username}` : `ID ${target.userId}`) : '';
  return (
    <>
      {link ? (
        <div className="uinspect-actions-row">
          <Button
            variant="danger"
            size="sm"
            onClick={() => open(link)}
            data-testid="user-telegram-unlink"
          >
            Telegram bağlantısını kaldır
          </Button>
        </div>
      ) : null}
      {done ? (
        <div className="uinspect-feedback">
          <Banner tone="success" testId="user-telegram-unlinked">
            Telegram bağlantısı kaldırıldı. Ödül geri verilmez, bu Telegram hesabı tekrar ödül alamaz.
          </Banner>
        </div>
      ) : null}
      <Modal
        open={target !== null}
        onClose={close}
        closeOnScrimClick={!running}
        closeOnEsc={!running}
        title="Telegram bağlantısını kaldır"
        width={480}
        testId="user-telegram-unlink-modal"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={running}>
              Vazgeç
            </Button>
            <Button
              variant="danger"
              onClick={() => void onConfirm()}
              disabled={!valid}
              loading={running}
              data-testid="user-telegram-unlink-confirm"
            >
              Bağlantıyı kaldır
            </Button>
          </>
        }
      >
        {target ? (
          <form className="uinspect-modal-body" onSubmit={onConfirm} aria-label="Telegram bağlantısını kaldır">
            <p className="uinspect-modal-text">
              <strong><SoftBreaks text={who} /></strong>
              {target.username ? <> (Telegram ID <span className="uinspect-mono">{target.userId}</span>)</> : null} bu
              hesaptan ayrılacak.
            </p>
            <Banner tone="warn" testId="user-telegram-unlink-warning">
              Ödül geri verilmez, bu Telegram hesabı tekrar ödül alamaz.
            </Banner>
            <Field
              label="Neden"
              htmlFor="tgUnlinkReason"
              help={`Audit'e geçer; en az ${UNLINK_REASON_MIN} karakter`}
              error={touched && !valid ? `Neden en az ${UNLINK_REASON_MIN} karakter olmalı.` : undefined}
            >
              <Input
                id="tgUnlinkReason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                invalid={touched && !valid}
                placeholder="örn. müşteri talebi #1234"
                autoComplete="off"
                disabled={running}
              />
            </Field>
            {mut.state.kind === 'error' ? (
              <Banner tone="danger" testId="user-telegram-unlink-error">
                {mut.state.reason === 'not_linked' ? (
                  'Bu hesabın Telegram bağlantısı zaten yok (başka bir yönetici kaldırmış olabilir).'
                ) : mut.state.reason === 'forbidden' ? (
                  'Bu işlem için user:adjust_balance yetkisi gerekiyor.'
                ) : (
                  <>
                    <code>telegram-unlink</code>: <code><SoftBreaks text={mut.state.reason} /></code>
                  </>
                )}
              </Banner>
            ) : null}
          </form>
        ) : null}
      </Modal>
    </>
  );
}

function IdentityCard({
  user,
  telegram,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly telegram: TelegramState;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const fullName = [user.profile.firstName, user.profile.lastName].filter(Boolean).join(' ');
  return (
    <Card flush data-testid="user-identity-card">
      <CardHeader title="Kimlik" />
      <CardBody>
        <InfoList testId="user-identity-list" label="Kimlik bilgileri">
          <InfoItem label="E-posta">
            {user.email ? (
              <>
                <span className="uinspect-info-text"><SoftBreaks text={user.email} /></span>
                <CopyButton value={user.email} label="E-postayı kopyala" />
              </>
            ) : (
              <span className="uinspect-muted">—</span>
            )}
          </InfoItem>
          <InfoItem label="Kullanıcı adı">
            {user.username ? (
              <>
                <span className="uinspect-info-text"><SoftBreaks text={user.username} /></span>
                <CopyButton value={user.username} label="Kullanıcı adını kopyala" />
              </>
            ) : (
              <span className="uinspect-muted">—</span>
            )}
          </InfoItem>
          <InfoItem label="Ad / Soyad">
            {fullName ? <span className="uinspect-info-text">{fullName}</span> : <span className="uinspect-muted">—</span>}
          </InfoItem>
          <InfoItem label="Rol">
            <span className="uinspect-info-text">{user.role}</span>
          </InfoItem>
          <InfoItem label="Kayıt" mono>
            {fmtDate(user.createdAt)}
          </InfoItem>
          <InfoItem label="Son giriş" mono>
            {user.lastLogin ? fmtDate(user.lastLogin) : <span className="uinspect-muted">Kayıt yok</span>}
          </InfoItem>
          {user.isBanned ? (
            <InfoItem label="Ban nedeni" wide>
              <span className="uinspect-info-text">{user.banReason ?? '—'}</span>
              {user.bannedAt ? <span className="uinspect-mono uinspect-muted">{fmtDate(user.bannedAt)}</span> : null}
            </InfoItem>
          ) : null}
          {user.isDeleted ? (
            <InfoItem label="Silinme nedeni" wide>
              <span className="uinspect-info-text">{user.deleteReason ?? '—'}</span>
              {user.deletedAt ? <span className="uinspect-mono uinspect-muted">{fmtDate(user.deletedAt)}</span> : null}
            </InfoItem>
          ) : null}
          <InfoItem label="Actor id" wide>
            <IdMono value={user.actorId} display={user.actorId} />
          </InfoItem>
          {/* Admin-only free-text note (never shown to the customer); edited inline. */}
          <AdminNoteSection key={actorId} user={user} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
          {/* Telegram channel reward (C2): username, id, link, membership, reward. */}
          <TelegramInfoItems telegram={telegram} />
        </InfoList>
        {canAdjust ? <TelegramUnlinkAction telegram={telegram} actorId={actorId} onChanged={onChanged} /> : null}
      </CardBody>
    </Card>
  );
}

/** Compact Turkish relative time for the presence line. */
function presenceRelative(iso: string | null): string {
  if (!iso) return 'Kayıt yok';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Kayıt yok';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return d.toLocaleString();
}

function statusBadgeFor(user: WireAdminUserDetail): JSX.Element {
  if (user.isBanned) return <Badge tone="danger" dot>banlı</Badge>;
  if (user.isDeleted) return <Badge tone="danger" dot>silinmiş</Badge>;
  if (!user.isActive) return <Badge tone="warn" dot>pasif</Badge>;
  return <Badge tone="success" dot>aktif</Badge>;
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}

function BalanceCard({
  user,
  topups,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly topups: AdminUserDetailResponse['data']['topups'];
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'add' | 'subtract' | 'set'>('add');
  const [reason, setReason] = useState('');
  const mutation = useApiMutation<AdminBalanceEditInput, AdminBalanceEditResponse>(
    (opts, input) => editAdminUserBalance(opts, actorId, input),
  );
  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!canAdjust) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt)) return;
    const res = await mutation.run({ amount: amt, type, reason: reason.trim() });
    if (res.kind === 'ok') {
      setAmount('');
      setReason('');
      onChanged();
    }
  }
  return (
    <Card flush data-testid="user-balance-card">
      <CardHeader
        title="Bakiye ve yüklemeler"
        actions={<span className="uinspect-amount">${user.balance.toFixed(2)}</span>}
      />
      <CardBody>
        {/* "When did this account last put money in?" — the single most-asked
            question about a customer, and it was not answerable anywhere on
            the admin surface. Ledger credits, not tier metrics: an admin grant
            or a reconciled payment never moves the tier ladder. */}
        <TopupSummary topups={topups} />
        <form onSubmit={onSubmit} aria-label="bakiye düzenle" className="uinspect-form-section">
          <div className="uinspect-form-grid">
            <Field label="İşlem" htmlFor="balType">
              <Select id="balType" value={type} onChange={(e) => setType(e.target.value as typeof type)} disabled={!canAdjust}>
                <option value="add">Ekle</option>
                <option value="subtract">Düş</option>
                <option value="set">Ayarla</option>
              </Select>
            </Field>
            <Field label="Miktar ($)" htmlFor="balAmt">
              <Input
                id="balAmt"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={!canAdjust}
                placeholder="örn. 10.50"
              />
            </Field>
          </div>
          <div className="uinspect-form-row">
            <Field label="Neden" htmlFor="balReason" help="Audit'e geçer; zorunlu">
              <Input
                id="balReason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={!canAdjust}
                placeholder="örn. müşteri talebi #1234"
              />
            </Field>
          </div>
          <div className="uinspect-actions-row">
            <Button
              type="submit"
              variant="primary"
              loading={mutation.state.kind === 'running'}
              disabled={!canAdjust || !amount || !reason.trim()}
            >
              Bakiyeyi güncelle
            </Button>
          </div>
        </form>
        {mutation.state.kind === 'error' ? (
          <div className="uinspect-feedback">
            <Banner tone="danger" testId="balance-edit-error">
              <code>balance</code>: <code><SoftBreaks text={mutation.state.reason} /></code>
            </Banner>
          </div>
        ) : null}
        {mutation.state.kind === 'ok' ? (
          <div className="uinspect-feedback">
            <Banner tone="success">
              Bakiye güncellendi: ${mutation.state.data.data.user.balanceBefore.toFixed(2)} → ${mutation.state.data.data.user.balanceAfter.toFixed(2)}
              {' '}(fark ${mutation.state.data.data.user.difference.toFixed(2)})
            </Banner>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Top-up history in three lines: when last, how much, how many in total.
 * Renders an explicit "hiç bakiye yüklememiş" rather than an empty block —
 * a customer who never paid is itself the answer to most support questions.
 */
function TopupSummary({
  topups,
}: {
  readonly topups: AdminUserDetailResponse['data']['topups'];
}): JSX.Element {
  if (!topups) {
    // Older API build that predates the field — say so instead of implying zero.
    return <div className="t-xs t-tertiary">Yükleme geçmişi bu API sürümünde yok.</div>;
  }
  if (topups.count === 0) {
    return (
      <div className="dl-rows" data-testid="user-topup-summary">
        <Row k="Son yükleme" v={<Badge tone="warn" dot>hiç bakiye yüklememiş</Badge>} />
      </div>
    );
  }
  return (
    <div className="dl-rows" data-testid="user-topup-summary">
      <Row
        k="Son yükleme"
        v={
          <span>
            <strong className="t-mono t-num">{microToUsd(topups.lastAmountMicro ?? 0)}</strong>{' '}
            <span className="t-xs">· {fmtDate(topups.lastAt)}</span>{' '}
            <span className="t-xs t-tertiary">({sinceLabel(topups.lastAt)})</span>
          </span>
        }
      />
      <Row
        k="Yükleme sayısı"
        v={
          <span>
            <span className="t-mono t-num">{topups.count}</span>{' '}
            <span className="t-xs t-tertiary">
              toplam {microToUsd(topups.totalMicro)}
              {topups.lastProvider ? ` · ${topups.lastProvider}` : ''}
            </span>
          </span>
        }
      />
      <Row k="İlk yükleme" v={<span className="t-mono t-xs">{fmtDate(topups.firstAt)}</span>} />
    </div>
  );
}

/** "12 gün önce" for a past timestamp; the age is what an operator reads. */
function sinceLabel(iso: string | null): string {
  if (!iso) return 'kayıt yok';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'kayıt yok';
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  return `${Math.floor(hr / 24)} gün önce`;
}

/** Tier index → select option label using the resolved tier name when present. */
const TIER_OVERRIDE_OPTIONS = [
  { value: '', label: 'Otomatik (eşik bazlı)' },
  { value: '1', label: 'Kademe 1 (zorla)' },
  { value: '2', label: 'Kademe 2 (zorla)' },
  { value: '3', label: 'Kademe 3 (zorla)' },
  { value: '4', label: 'Kademe 4 (zorla)' },
  { value: '5', label: 'Kademe 5 (zorla)' },
];

function TierRow({ tier, totalMicro }: { readonly tier: WireUserTierView['lifetime']; readonly totalMicro: number }): JSX.Element {
  return (
    <div className="dl-rows">
      <Row k="Kademe" v={<span>{tier.name ?? '—'}{tier.source === 'override' ? ' (override)' : ''}</span>} />
      <Row k="İndirim" v={<span className="t-mono t-num">{bpsToPct(tier.discountBps)}</span>} />
      <Row k="Toplam" v={<span className="t-mono t-num">{microToUsd(totalMicro)}</span>} />
      <Row
        k="Sonraki kademe"
        v={
          tier.nextTierName && tier.nextThresholdMicro != null ? (
            <span className="t-xs">
              {tier.nextTierName} @ {microToUsd(tier.nextThresholdMicro)}
            </span>
          ) : (
            <span className="t-xs t-tertiary">En üst kademe</span>
          )
        }
      />
    </div>
  );
}

function TierCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const tier = user.tier;
  const [ol, setOl] = useState(tier.overrideLifetimeTier == null ? '' : String(tier.overrideLifetimeTier));
  const [ow, setOw] = useState(tier.overrideWeeklyTier == null ? '' : String(tier.overrideWeeklyTier));
  const mut = useApiMutation<
    { overrideLifetimeTier: number | null; overrideWeeklyTier: number | null },
    AdminTierOverrideResponse
  >((opts, body) => setAdminUserTierOverride(opts, actorId, body));
  async function onSave(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!canAdjust) return;
    const res = await mut.run({
      overrideLifetimeTier: ol === '' ? null : Number(ol),
      overrideWeeklyTier: ow === '' ? null : Number(ow),
    });
    if (res.kind === 'ok') onChanged();
  }
  return (
    <Card flush data-testid="user-tier-card">
      <CardHeader
        title="Seviye (Kalıcı / Haftalık)"
        actions={<Badge tone={tier.effectiveDiscountBps > 0 ? 'success' : 'neutral'} dot>
          Uygulanan: {bpsToPct(tier.effectiveDiscountBps)}
        </Badge>}
      />
      <CardBody>
        <div className="uinspect-tier-cols">
          <div>
            <div className="uinspect-subhead">Kalıcı Seviye</div>
            <TierRow tier={tier.lifetime} totalMicro={tier.lifetimeTotalMicro} />
          </div>
          <div>
            <div className="uinspect-subhead">Haftalık Seviye</div>
            <TierRow tier={tier.weekly} totalMicro={tier.weekTotalMicro} />
          </div>
        </div>
        <div className="uinspect-note">
          Uygulanan indirim = Kalıcı + Haftalık (toplanır, en fazla %50).
          {tier.effectiveScope ? ` Baskın: ${tier.effectiveScope === 'lifetime' ? 'Kalıcı' : 'Haftalık'}.` : ''}
        </div>
        <form onSubmit={onSave} aria-label="seviye override" className="uinspect-form-section">
          <div className="uinspect-form-grid">
            <Field label="Kalıcı override" htmlFor="ol">
              <Select id="ol" value={ol} onChange={(e) => setOl(e.target.value)} disabled={!canAdjust}>
                {TIER_OVERRIDE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
            <Field label="Haftalık override" htmlFor="ow">
              <Select id="ow" value={ow} onChange={(e) => setOw(e.target.value)} disabled={!canAdjust}>
                {TIER_OVERRIDE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="uinspect-actions-row">
            <Button type="submit" variant="primary" size="sm" disabled={!canAdjust} loading={mut.state.kind === 'running'}>
              Override kaydet
            </Button>
          </div>
        </form>
        {mut.state.kind === 'error' ? (
          <div className="uinspect-feedback"><Banner tone="danger"><code>tier</code>: <code><SoftBreaks text={mut.state.reason} /></code></Banner></div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function RefundCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const refundMut = useApiMutation<boolean, AdminRefundStatusResponse>(
    (opts, disabled) => setAdminUserRefundStatus(opts, actorId, disabled),
  );
  async function onRefundToggle(): Promise<void> {
    if (!canAdjust) return;
    const res = await refundMut.run(!user.refundDisabled);
    if (res.kind === 'ok') onChanged();
  }
  return (
    <Card flush data-testid="user-refund-card">
      <CardHeader
        title="İade hakkı"
        actions={
          user.refundDisabled ? (
            <Badge tone="danger" dot>İade kapalı</Badge>
          ) : (
            <Badge tone="success" dot>İade açık</Badge>
          )
        }
      />
      <CardBody>
        <div className="uinspect-toggle-row">
          <div className="uinspect-toggle-text">
            {user.refundDisabled ? 'Müşteri iade talep edemez' : 'Müşteri iade talep edebilir'}
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canAdjust}
            loading={refundMut.state.kind === 'running'}
            onClick={onRefundToggle}
          >
            {user.refundDisabled ? 'İadeyi aç' : 'İadeyi kapat'}
          </Button>
        </div>
        {refundMut.state.kind === 'error' ? (
          <div className="uinspect-feedback"><Banner tone="danger"><code>refund</code>: <code><SoftBreaks text={refundMut.state.reason} /></code></Banner></div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ModerationCard({
  user,
  canBan,
  canUnban,
  canDelete,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canBan: boolean;
  readonly canUnban: boolean;
  readonly canDelete: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const banMut = useApiMutation<ModerationInput, ModerationResponse>(banUser);
  const unbanMut = useApiMutation<ModerationInput, ModerationResponse>(unbanUser);
  const deleteMut = useApiMutation<ModerationInput, ModerationResponse>(softDeleteUser);
  async function run(action: 'ban' | 'unban' | 'delete'): Promise<void> {
    if (!reason.trim()) return;
    const fn = action === 'ban' ? banMut.run : action === 'unban' ? unbanMut.run : deleteMut.run;
    const res = await fn({ actorId, reason: reason.trim() });
    if (res.kind === 'ok') {
      setReason('');
      onChanged();
    }
  }
  return (
    <Card flush className="uinspect-moderation" data-testid="user-moderation-card">
      <CardHeader title="Moderasyon" subtitle="Ban, ban kaldırma ve soft delete" />
      <CardBody>
        <div className="uinspect-moderation-form">
          <Field label="Neden" htmlFor="modReason" help="Audit'e geçer; tüm aksiyonlar için zorunlu">
            <Input
              id="modReason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="örn. müşteri talebi / şüpheli aktivite"
            />
          </Field>
        </div>
        <div className="uinspect-actions-row">
          <Button
            variant="danger"
            disabled={!canBan || user.isBanned || !reason.trim()}
            loading={banMut.state.kind === 'running'}
            onClick={() => run('ban')}
          >
            Banla
          </Button>
          <Button
            variant="secondary"
            disabled={!canUnban || !user.isBanned || !reason.trim()}
            loading={unbanMut.state.kind === 'running'}
            onClick={() => run('unban')}
          >
            Banı kaldır
          </Button>
          <Button
            variant="danger"
            disabled={!canDelete || user.isDeleted || !reason.trim()}
            loading={deleteMut.state.kind === 'running'}
            onClick={() => run('delete')}
          >
            Soft delete
          </Button>
        </div>
        {banMut.state.kind === 'error' ? <ErrBanner label="ban" reason={banMut.state.reason} /> : null}
        {unbanMut.state.kind === 'error' ? <ErrBanner label="unban" reason={unbanMut.state.reason} /> : null}
        {deleteMut.state.kind === 'error' ? <ErrBanner label="delete" reason={deleteMut.state.reason} /> : null}
      </CardBody>
    </Card>
  );
}

function ApiKeyCard({
  apiKeys,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly apiKeys: readonly WireAdminUserDetailApiKey[];
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const active = useMemo(() => apiKeys.find((k) => k.revokedAt === null) ?? null, [apiKeys]);
  const regenMut = useApiMutation<string | null, AdminApiKeyRegenerateResponse>(
    (opts, label) => regenerateAdminUserApiKey(opts, actorId, label),
  );
  const revokeMut = useApiMutation<null, AdminApiKeyRevokeResponse>(
    (opts) => revokeAdminUserApiKey(opts, actorId),
  );
  const revealMut = useApiMutation<null, AdminApiKeyRevealResponse>(
    (opts) => revealAdminUserApiKey(opts, actorId),
  );

  async function onRegen(): Promise<void> {
    if (!canAdjust) return;
    if (!window.confirm('Yeni anahtar üretilecek ve mevcut anahtar iptal olacak. Devam?')) return;
    const res = await regenMut.run(null);
    if (res.kind === 'ok') onChanged();
  }
  async function onRevoke(): Promise<void> {
    if (!canAdjust) return;
    if (!window.confirm('Aktif API anahtarı iptal edilecek. Devam?')) return;
    const res = await revokeMut.run(null);
    if (res.kind === 'ok') onChanged();
  }
  async function onReveal(): Promise<void> {
    await revealMut.run(null);
  }

  return (
    <Card flush data-testid="user-api-key-card">
      <CardHeader title="API Anahtarı" />
      <CardBody>
        <div className="dl-rows">
          <div className="dl-row">
            <div className="dl-key">Aktif anahtar</div>
            <div className="dl-val">
              {active ? (
                <span className="uinspect-inline-values">
                  <IdMono value={active.prefix} noCopy display={`${active.prefix}…`} />
                  <span className="uinspect-mono uinspect-muted">id={active.id.slice(0, 8)}…</span>
                </span>
              ) : (
                <span className="t-tertiary">yok</span>
              )}
            </div>
          </div>
          {active ? (
            <div className="dl-row">
              <div className="dl-key">Son kullanım</div>
              <div className="dl-val uinspect-mono">{fmtDate(active.lastUsedAt)}</div>
            </div>
          ) : null}
        </div>
        <div className="uinspect-actions-row">
          <Button variant="secondary" size="sm" onClick={onReveal} loading={revealMut.state.kind === 'running'} icon={<Icon.Refresh size={14} />}>
            Yenile
          </Button>
          <Button variant="primary" size="sm" disabled={!canAdjust} loading={regenMut.state.kind === 'running'} onClick={onRegen} icon={<Icon.Key size={14} />}>
            Yeni anahtar üret
          </Button>
          <Button variant="danger" size="sm" disabled={!canAdjust || !active} loading={revokeMut.state.kind === 'running'} onClick={onRevoke}>
            İptal et
          </Button>
        </div>
        {regenMut.state.kind === 'ok' ? (
          <div className="uinspect-feedback" data-testid="api-key-reveal">
            <Banner tone="warn" title="Yeni anahtar (yalnızca BU yanıtta görünür)">
              <code style={{ wordBreak: 'break-all' }}>{regenMut.state.data.data.apiKey}</code>
              <div className="t-xs t-tertiary" style={{ marginTop: 6 }}>
                Hemen güvenli bir yere kaydedin. Sayfa yenilenince bir daha gösterilmez.
              </div>
            </Banner>
          </div>
        ) : null}
        {regenMut.state.kind === 'error' ? <ErrBanner label="api-key-regen" reason={regenMut.state.reason} /> : null}
        {revokeMut.state.kind === 'error' ? <ErrBanner label="api-key-revoke" reason={revokeMut.state.reason} /> : null}
      </CardBody>
    </Card>
  );
}

function RecentOrdersCard({
  orders,
}: {
  readonly orders: readonly WireAdminUserDetailOrder[];
}): JSX.Element {
  return (
    <Card flush>
      <CardHeader title="Son 10 sipariş" />
      <CardBody tight style={{ padding: orders.length === 0 ? 20 : 0 }}>
        {orders.length === 0 ? (
          <EmptyState label="Bu kullanıcının siparişi yok." />
        ) : (
          <Table dense stack className="tbl-stack-xl users-orders-table">
            <THead>
              <TRow>
                <TH>Sipariş</TH>
                {/* The site is what the customer actually bought; without it
                    every row in this table looked identical. */}
                <TH>Site</TH>
                <TH>Kiralanan kutu</TH>
                <TH>Durum</TH>
                <TH>Mail</TH>
                <TH>Sağlayıcı</TH>
                <TH>Tutar</TH>
                <TH>Açılış</TH>
              </TRow>
            </THead>
            <TBody>
              {orders.map((o) => (
                <TRow key={o.orderId}>
                  <TD label="Sipariş" className="tbl-stack-primary">
                    <Link className="users-order-link" to={`/orders/${encodeURIComponent(o.orderId)}`}>
                      <IdMono value={o.orderId} />
                    </Link>
                  </TD>
                  <TD label="Site">
                    {o.siteLabel ?? o.siteKey ?? <span className="t-tertiary">—</span>}
                  </TD>
                  <TD label="Kiralanan kutu" className="t-mono t-xs users-cell-mailbox">
                    {o.mailboxAddress != null ? <SoftBreaks text={o.mailboxAddress} /> : '—'}
                  </TD>
                  <TD label="Durum" className="tbl-stack-status"><StatusBadge status={o.state} /></TD>
                  {/* D-232 — inline "mail/code arrived?" per order (provider_inbox_messages). */}
                  <TD
                    label="Mail"
                    data-touch-detail={o.codeReceived && o.codeReceivedAt ? fmtDate(o.codeReceivedAt) : undefined}
                  >
                    {o.codeReceived ? (
                      <Badge tone="success" dot title={o.codeReceivedAt ? fmtDate(o.codeReceivedAt) : 'mail geldi'}>geldi</Badge>
                    ) : (
                      <Badge tone="neutral" dot>bekliyor</Badge>
                    )}
                  </TD>
                  <TD label="Sağlayıcı" className="t-mono t-xs">{o.providerId ?? '—'}</TD>
                  <TD label="Tutar" className="t-mono t-num">{orderAmount(o)}</TD>
                  <TD label="Açılış" className="t-mono t-xs t-tertiary">{fmtDate(o.openedAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function RecentTransactionsCard({
  transactions,
}: {
  readonly transactions: readonly WireAdminUserDetailTransaction[];
}): JSX.Element {
  return (
    <Card flush>
      <CardHeader title="Son 10 işlem" />
      <CardBody tight style={{ padding: transactions.length === 0 ? 20 : 0 }}>
        {transactions.length === 0 ? (
          <EmptyState label="Bu kullanıcının işlemi yok." />
        ) : (
          <Table dense stack className="tbl-stack-lg users-tx-table">
            <THead>
              <TRow>
                <TH>Tip</TH>
                <TH>Tutar</TH>
                <TH>Durum</TH>
                <TH>Açıklama</TH>
                <TH>Tarih</TH>
              </TRow>
            </THead>
            <TBody>
              {transactions.map((t) => (
                <TRow key={t.id}>
                  <TD label="Tip" className="t-mono t-xs tbl-stack-primary">{t.type}</TD>
                  <TD label="Tutar" className="t-mono t-num cell-nowrap">${t.amount.toFixed(2)} {t.currency}</TD>
                  <TD label="Durum" className="tbl-stack-status"><StatusBadge status={t.status} /></TD>
                  <TD label="Açıklama" className="tbl-stack-full users-cell-desc">{t.description ?? '—'}</TD>
                  <TD label="Tarih" className="t-mono t-xs t-tertiary">{fmtDate(t.createdAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function ErrBanner({ label, reason }: { readonly label: string; readonly reason: string }): JSX.Element {
  return (
    <div className="uinspect-feedback">
      <Banner tone="danger"><code>{label}</code>: <code><SoftBreaks text={reason} /></code></Banner>
    </div>
  );
}

/**
 * What this order actually cost. `finalPriceMinor` comes from checkout_intents,
 * which is empty for balance-paid orders — that is why this column rendered "—"
 * on every row — and cents floor a $0.002 sale to $0.00 anyway. The ledger's
 * micro amount is the truth; the cent view is only a fallback for old builds.
 */
function orderAmount(o: WireAdminUserDetailOrder): string {
  if (o.chargedMicro != null && o.chargedMicro > 0) {
    const net = o.chargedMicro - (o.refundedMicro ?? 0);
    const label = `$${(o.chargedMicro / 1_000_000).toFixed(4)}`;
    return net <= 0 ? `${label} (iade)` : label;
  }
  if (o.finalPriceMinor !== null && o.finalPriceMinor !== undefined) {
    return `$${(o.finalPriceMinor / 100).toFixed(2)}`;
  }
  return '—';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR');
  } catch {
    return iso;
  }
}
